import { Capacitor } from '@capacitor/core';
import {
  NativeBiometric,
  BiometryType,
} from 'capacitor-native-biometric';
import { get, set, remove } from './Preferences';

// Identifiers under which the session credentials are stored in the native
// Keychain (iOS) / Keystore (Android). Kept stable so we can read/delete the
// same entries across launches. The EAM refresh token and the Portal refresh
// token are stored under separate keys so biometric can silently refresh both.
const BIOMETRIC_SERVER = 'com.aptean.eam.biometric';
export const BIOMETRIC_PORTAL_SERVER = 'com.aptean.eam.biometric.portal';

// Non-secret preference flag recording whether the user opted in to
// biometric sign-in. The actual token lives in the secure store above.
export const _biometricEnabledKey = 'biometricEnabled';

export interface BiometricAvailability {
  available: boolean;
  biometryType: BiometryType;
  /** Device-appropriate label, e.g. "Face ID", "Touch ID", "Fingerprint". */
  label: string;
}

/**
 * Maps the platform biometry type to a user-facing label. Apple-branded terms
 * ("Face ID" / "Touch ID") are used ONLY for the iOS types where they are the
 * correct, device-accurate name; Android types use neutral, Android-appropriate
 * wording ("Face Unlock" / "Fingerprint"), never Apple branding. Unknown/mixed
 * types fall back to the generic "Biometric".
 */
export const biometryLabel = (type: BiometryType): string => {
  switch (type) {
    case BiometryType.FACE_ID:
      return 'Face ID'; // iOS
    case BiometryType.TOUCH_ID:
      return 'Touch ID'; // iOS
    case BiometryType.FACE_AUTHENTICATION:
      return 'Face Unlock'; // Android - avoid the Apple "Face ID" term
    case BiometryType.FINGERPRINT:
      return 'Fingerprint';
    case BiometryType.IRIS_AUTHENTICATION:
      return 'Iris';
    case BiometryType.MULTIPLE:
      return 'Biometrics';
    default:
      return 'Biometric';
  }
};

/**
 * Reports whether biometric authentication can be used on this device.
 * Always false on web/PWA - the native plugin has no web implementation,
 * so callers fall back to the standard B2C login flow.
 */
export const isBiometricAvailable = async (): Promise<BiometricAvailability> => {
  const unavailable: BiometricAvailability = {
    available: false,
    biometryType: BiometryType.NONE,
    label: biometryLabel(BiometryType.NONE),
  };
  if (!Capacitor.isNativePlatform()) {
    return unavailable;
  }
  try {
    // useFallback lets the OS offer device passcode when biometrics are
    // unavailable/unenrolled, per the user's device settings.
    const result = await NativeBiometric.isAvailable({ useFallback: true });
    return {
      available: result.isAvailable,
      biometryType: result.biometryType,
      label: biometryLabel(result.biometryType),
    };
  } catch (error) {
    console.log('isBiometricAvailable error:', error);
    return unavailable;
  }
};

/**
 * Presents the native biometric prompt (Face ID / Touch ID / fingerprint,
 * with automatic passcode fallback per device settings).
 * Resolves true on success, false if the user cancels or verification fails.
 */
export const promptBiometric = async (reason: string): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }
  try {
    await NativeBiometric.verifyIdentity({
      reason,
      title: 'EAM Mobile',
      subtitle: reason,
      useFallback: true,
    });
    return true;
  } catch (error) {
    // Thrown on cancel, lockout, or failed verification - all treated as "not verified".
    console.log('promptBiometric not verified:', error);
    return false;
  }
};

/**
 * Securely stores the session token (access token now, refresh token once
 * B2C offline_access is enabled) in the device Keychain/Keystore.
 */
export const storeBiometricCredential = async (
  token: string,
  username: string,
  server: string = BIOMETRIC_SERVER,
): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }
  try {
    await NativeBiometric.setCredentials({
      username: username || 'eam-user',
      password: token,
      server,
    });
    console.log('storeBiometricCredential: credential saved for', server);
    return true;
  } catch (error) {
    console.log('storeBiometricCredential error:', error);
    return false;
  }
};

/**
 * Retrieves the securely stored session token, or null if none is present.
 */
export const getBiometricCredential = async (
  server: string = BIOMETRIC_SERVER,
): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  try {
    const credentials = await NativeBiometric.getCredentials({ server });
    return credentials?.password || null;
  } catch (error) {
    console.log('getBiometricCredential error:', error);
    return null;
  }
};

/**
 * Removes the securely stored session token. Safe to call when none exists.
 */
export const deleteBiometricCredential = async (
  server: string = BIOMETRIC_SERVER,
): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    await NativeBiometric.deleteCredentials({ server });
  } catch (error) {
    console.log('deleteBiometricCredential error:', error);
  }
};

/**
 * Whether the user has opted in to biometric sign-in.
 */
export const isBiometricEnabled = async (): Promise<boolean> => {
  const value = await get(_biometricEnabledKey);
  return value === true;
};

/**
 * Records the user's biometric opt-in choice. Disabling also clears the
 * securely stored credential so nothing is left behind.
 */
export const setBiometricEnabled = async (enabled: boolean): Promise<void> => {
  if (enabled) {
    await set(_biometricEnabledKey, true);
  } else {
    await remove(_biometricEnabledKey);
    // Remove BOTH stored credentials (EAM + Portal) so nothing is left behind.
    await deleteBiometricCredential();
    await deleteBiometricCredential(BIOMETRIC_PORTAL_SERVER);
  }
};
